<template>
  <el-dialog
    title="手机扫码签名"
    :visible.sync="visible"
    width="420px"
    append-to-body
    custom-class="hand-sign-dialog"
    @opened="onOpened"
    @closed="onClosed"
  >
    <div v-if="phase === 'loading'" class="qr-state">正在生成二维码…</div>
    <div v-else-if="phase === 'error'" class="qr-state qr-error">{{ errMsg || '生成失败' }}</div>
    <div v-else-if="phase === 'done'" class="qr-state qr-ok">签名已收到</div>
    <div v-else class="qr-wrap">
      <div class="qr-img-box">
        <img v-if="qrImg" :src="qrImg" alt="扫码签名" class="qr-img" />
        <div v-else class="qr-state">二维码加载中…</div>
      </div>
      <p class="qr-tip">请用手机扫描二维码完成手写签名</p>
      <p class="qr-expire">链接 {{ ttlMin }} 分钟内有效，签完即失效</p>
      <p v-if="remainText" class="qr-remain">剩余 {{ remainText }}</p>
      <el-button type="text" size="small" @click="recreate">刷新二维码</el-button>
    </div>
    <div slot="footer" class="hand-sign-footer">
      <el-button icon="el-icon-close" @click="visible = false">取消</el-button>
    </div>
  </el-dialog>
</template>

<script>
import api from '@/utils/api'

export default {
  name: 'HandSignDialog',
  props: {
    value: { type: Boolean, default: false },
    /** 已有签名完整 URL（保留兼容，扫码流程不展示画板） */
    existingUrl: { type: String, default: '' },
    xmbh: { type: String, default: '' },
    taskId: { type: String, default: '' }
  },
  data() {
    return {
      phase: 'loading', // loading | waiting | done | error
      errMsg: '',
      token: '',
      signUrl: '',
      qrImg: '',
      expireAt: '',
      ttlSec: 300,
      pollTimer: null,
      tickTimer: null,
      remainSec: 0
    }
  },
  computed: {
    visible: {
      get() { return this.value },
      set(v) { this.$emit('input', v) }
    },
    ttlMin() {
      return Math.max(1, Math.round((this.ttlSec || 300) / 60))
    },
    remainText() {
      const s = this.remainSec
      if (s <= 0) return ''
      const m = Math.floor(s / 60)
      const sec = s % 60
      return m + ':' + String(sec).padStart(2, '0')
    }
  },
  methods: {
    onOpened() {
      this.createSession()
    },
    onClosed() {
      this.stopTimers()
      this.phase = 'loading'
      this.errMsg = ''
      this.token = ''
      this.signUrl = ''
      this.qrImg = ''
      this.expireAt = ''
      this.remainSec = 0
    },
    stopTimers() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      if (this.tickTimer) {
        clearInterval(this.tickTimer)
        this.tickTimer = null
      }
    },
    async recreate() {
      this.stopTimers()
      await this.createSession()
    },
    async createSession() {
      this.phase = 'loading'
      this.errMsg = ''
      this.qrImg = ''
      try {
        const res = await api.post('yn_sign_session_create', {
          xmbh: this.xmbh || '',
          task_id: this.taskId || '',
          purpose: 'pick_auditor'
        })
        const data = res.data || {}
        if (!data.ok || !data.token || !data.sign_url) {
          throw new Error(data.error || '创建签名会话失败')
        }
        this.token = data.token
        this.signUrl = data.sign_url
        this.expireAt = data.expire_at || ''
        this.ttlSec = Number(data.ttl_sec) || 300
        this.qrImg =
          'https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=' +
          encodeURIComponent(this.signUrl)
        this.phase = 'waiting'
        this.syncRemain()
        this.startTimers()
      } catch (e) {
        this.phase = 'error'
        this.errMsg = (e && e.message) || '生成二维码失败'
      }
    },
    syncRemain() {
      if (!this.expireAt) {
        this.remainSec = 0
        return
      }
      const t = Date.parse(String(this.expireAt).replace(/-/g, '/'))
      if (!t) {
        this.remainSec = 0
        return
      }
      this.remainSec = Math.max(0, Math.floor((t - Date.now()) / 1000))
      if (this.remainSec <= 0 && this.phase === 'waiting') {
        this.phase = 'error'
        this.errMsg = '二维码已过期，请刷新'
        this.stopTimers()
      }
    },
    startTimers() {
      this.stopTimers()
      this.pollTimer = setInterval(() => this.pollOnce(), 1500)
      this.tickTimer = setInterval(() => this.syncRemain(), 1000)
      this.pollOnce()
    },
    async pollOnce() {
      if (!this.token || this.phase !== 'waiting') return
      try {
        const res = await api.get('yn_sign_session_status', { token: this.token })
        const data = res.data || {}
        const sess = data.session || {}
        if (!data.ok) return
        if (sess.status === 'used' && sess.sign_img) {
          this.phase = 'done'
          this.stopTimers()
          this.$emit('signed', sess.sign_img)
          this.visible = false
          return
        }
        if (sess.status === 'expired') {
          this.phase = 'error'
          this.errMsg = '二维码已过期，请刷新'
          this.stopTimers()
        }
      } catch (e) {
        /* 轮询失败忽略，下一拍再试 */
      }
    }
  }
}
</script>

<style scoped>
.qr-wrap {
  text-align: center;
  padding: 8px 0 4px;
}
.qr-img-box {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
}
.qr-img {
  width: 240px;
  height: 240px;
  display: block;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
}
.qr-tip {
  margin: 16px 0 4px;
  font-size: 14px;
  color: #303133;
}
.qr-expire,
.qr-remain {
  margin: 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}
.qr-state {
  text-align: center;
  padding: 48px 16px;
  color: #606266;
  font-size: 14px;
}
.qr-error {
  color: #f56c6c;
}
.qr-ok {
  color: #67c23a;
}
.hand-sign-footer {
  text-align: right;
}
</style>
